<template>
  <div class="market-reports-page">
    <div class="d-flex flex-wrap align-items-center mb-2 mt-1">
      <h1 class="mb-0 mr-2">Market Reports</h1>
      <b-button
        variant="outline-secondary"
        size="sm"
        class="d-flex align-items-center"
        :href="`${baseURL}downloaddatareportguidepdf.cfm`"
        target="_blank"
        rel="noopener"
      >
        <b-img
          :src="require('@/assets/images/icons/file-icons/adobe_pdf_icon_modern.png')"
          height="20"
          style="width: auto;"
          alt="PDF"
        />
        <span
          class="ml-50 font-weight-bold text-body"
          style="position: relative; top: 2px;"
        >Guide to Market Reports</span>
      </b-button>
    </div>

    <div v-if="isLoggedIn && isPremium">
      <template v-if="hasReports">
        <b-tabs
          pills
          class="mb-2"
        >
          <b-tab
            v-for="tab in tabs"
            :key="tab"
            :title="String(tab)"
            @click="selectedYear = tab"
          />
        </b-tabs>

        <div
          v-for="yearData in filteredReports"
          :key="yearData.year"
          class="year-row mb-2"
        >
          <b-card
            no-body
            class="year-card"
          >
            <b-card-header class="year-header">
              <h5 class="mb-0">{{ yearData.year }}</h5>
            </b-card-header>
            <b-card-body class="p-0">
              <div class="reports-grid">
                <div
                  v-for="slot in yearData.slots"
                  :key="slot.type"
                  class="report-section"
                >
                  <div class="col-label">{{ slot.type }}</div>
                  <template v-if="slot.item">
                    <b-button
                      variant="outline-dark"
                      class="pdf-button"
                      :href="`${baseURL}downloaddatareportpdf.cfm?id=${slot.item.ID}`"
                      target="_blank"
                    >
                      <b-img
                        :src="require('@/assets/images/icons/file-icons/adobe_pdf_icon_modern.png')"
                        class="pdf-icon pdf-icon-large"
                      />
                      <div class="pdf-name">{{ slot.item.TITLE }}</div>
                    </b-button>
                    <div class="date-published">
                      <span class="date-label">Date published</span>
                      <span class="date-value">{{ formatDate(slot.item.DATEPUBLISHED) }}</span>
                    </div>
                  </template>
                  <div
                    v-else
                    class="no-report"
                  >
                    Not available
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </template>

      <div
        v-else-if="!reportsLoading"
        class="d-flex flex-column justify-content-center align-items-center py-5"
      >
        <div class="py-3 mx-5 text-center">
          <p>No market reports are currently available</p>
        </div>

        <div class="mt-1">
          <b-button
            variant="primary"
            @click.prevent="$router.push({ name: 'home' })"
          >
            Back to Home
          </b-button>
        </div>
      </div>
    </div>
    <div v-else>
      <UpgradeToPremium />
    </div>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BButton, BImg, BTabs, BTab,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import UpgradeToPremium from '@/views/components/UpgradeToPremium.vue'

export default {
  components: {
    UpgradeToPremium,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BImg,
    BTabs,
    BTab,
  },
  data() {
    return {
      selectedYear: 'All',
      annualReports: [],
      quarterlyReports: [],
      reportsLoading: true,
      baseURL: process.env.VUE_APP_BACKEND_PATH,
    }
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'isPremium']),
    reportsByYear() {
      const yearSet = new Set();
      [...this.annualReports, ...this.quarterlyReports].forEach(r => {
        yearSet.add(r.YEAR)
      })
      const years = Array.from(yearSet).sort((a, b) => b - a)

      return years.map(year => {
        const annual = this.annualReports.find(r => r.YEAR === year) || null
        const quarterlyForYear = this.quarterlyReports.filter(r => r.YEAR === year)

        const q1 = quarterlyForYear.find(r => r.TYPE === 'quarter_1') || null
        const q2 = quarterlyForYear.find(r => r.TYPE === 'quarter_2') || null
        const q3 = quarterlyForYear.find(r => r.TYPE === 'quarter_3') || null
        const q4 = quarterlyForYear.find(r => r.TYPE === 'quarter_4') || null

        return {
          year,
          slots: [
            { type: 'Q1', item: q1 },
            { type: 'Q2', item: q2 },
            { type: 'Q3', item: q3 },
            { type: 'Q4', item: q4 },
            { type: 'Annual', item: annual },
          ],
        }
      })
    },
    tabs() {
      return ['All', ...this.reportsByYear.map(y => y.year)]
    },
    filteredReports() {
      if (this.selectedYear === 'All') return this.reportsByYear
      return this.reportsByYear.filter(y => y.year === this.selectedYear)
    },
    hasReports() {
      return this.reportsByYear.length > 0
    },
  },
  created() {
    this.loadReports()
  },
  methods: {
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    async loadReports() {
      this.reportsLoading = true
      try {
        const response = await this.$http.get('/public.cfc', {
          params: { method: 'getDataReports', returnformat: 'JSON' },
        })
        this.annualReports = response.data.content.ANNUAL || []
        this.quarterlyReports = response.data.content.QUARTERLY || []
      } finally {
        this.reportsLoading = false
      }
    },
  },
}
</script>

<style>
.market-reports-page .year-card {
  border-radius: 0.5rem;
  overflow: hidden;
}

.market-reports-page .year-header {
  background-color: #f8f8f8;
  border-bottom: 1px solid #e0e0e0;
  padding: 1rem 1.5rem;
}

.market-reports-page .year-header h5 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #222;
  letter-spacing: 0.03em;
}

.market-reports-page .reports-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
}

.market-reports-page .report-section {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-right: 1px solid #e8e8e8;
}

.market-reports-page .report-section:last-child {
  border-right: none;
}

@media (max-width: 991px) {
  .market-reports-page .reports-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .market-reports-page .report-section {
    border-right: 1px solid #e8e8e8;
    border-bottom: 1px solid #e8e8e8;
  }

  .market-reports-page .report-section:nth-child(3n) {
    border-right: none;
  }

  .market-reports-page .report-section:nth-last-child(-n+3):nth-child(3n+1),
  .market-reports-page .report-section:nth-last-child(-n+3):nth-child(3n+1) ~ .report-section {
    border-bottom: none;
  }
}

@media (max-width: 575px) {
  .market-reports-page .reports-grid {
    grid-template-columns: 1fr 1fr;
  }

  .market-reports-page .report-section {
    border-right: 1px solid #e8e8e8;
    border-bottom: 1px solid #e8e8e8;
  }

  .market-reports-page .report-section:nth-child(3n) {
    border-right: 1px solid #e8e8e8;
  }

  .market-reports-page .report-section:nth-child(even) {
    border-right: none;
  }

  .market-reports-page .report-section:nth-last-child(-n+2):nth-child(odd),
  .market-reports-page .report-section:nth-last-child(-n+2):nth-child(odd) ~ .report-section {
    border-bottom: none;
  }

  .market-reports-page .pdf-button {
    width: 100px;
    height: 110px;
  }
}

.market-reports-page .col-label {
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #444;
  margin-bottom: 1rem;
  align-self: flex-start;
}

.market-reports-page .pdf-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  width: 160px;
  height: 160px;
  border-radius: 12px !important;
  white-space: normal;
}

.market-reports-page .pdf-icon {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
}

.market-reports-page .pdf-name {
  text-align: center;
  font-weight: 500;
  font-size: 0.85rem;
  line-height: 1.4;
  word-break: break-word;
}

.market-reports-page .date-published {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0.6rem;
  gap: 0.1rem;
}

.market-reports-page .date-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
}

.market-reports-page .date-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #444;
}

.market-reports-page .no-report {
  font-size: 0.85rem;
  color: #aaa;
  margin-top: 0.5rem;
}

.market-reports-page .nav-pills .nav-link:not(.active):hover {
  background-color: rgba(26, 115, 232, 0.12);
  transition: all 0.2s ease;
}

.market-reports-page .nav-pills .nav-link {
  transition: all 0.2s ease;
}

/* Dark mode overrides */
.dark-layout .market-reports-page .year-header {
  background-color: #343d55;
  border-bottom-color: #3b4253;
}

.dark-layout .market-reports-page .year-header h5 {
  color: #d0d2d6;
}

.dark-layout .market-reports-page .report-section {
  border-right-color: #3b4253;
}

.dark-layout .market-reports-page .col-label {
  color: #b4b7bd;
}

.dark-layout .market-reports-page .date-label {
  color: #676d7d;
}

.dark-layout .market-reports-page .date-value {
  color: #d0d2d6;
}

.dark-layout .market-reports-page .no-report {
  color: #676d7d;
}

.dark-layout .market-reports-page .pdf-button.btn-outline-dark {
  color: #d0d2d6;
  border-color: #3b4253;
}

.dark-layout .market-reports-page .pdf-button.btn-outline-dark:hover {
  background-color: #3b4253;
  color: #fff;
}

.dark-layout .market-reports-page .nav-pills .nav-link:not(.active):hover {
  background-color: rgba(26, 115, 232, 0.2);
}

@media (max-width: 991px) {
  .dark-layout .market-reports-page .report-section {
    border-bottom-color: #3b4253;
  }
}

@media (max-width: 575px) {
  .dark-layout .market-reports-page .report-section {
    border-bottom-color: #3b4253;
  }
}
</style>
